import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { formatCurrency, formatCompact } from '@/lib/utils'

function Card({
  label, value, caption, tone = 'neutral',
}: {
  label: string; value: string; caption?: string
  tone?: 'neutral' | 'danger' | 'success'
}) {
  const captionClass =
    tone === 'danger' ? 'text-status-danger-accent'
    : tone === 'success' ? 'text-status-approved-accent'
    : 'text-ink-tertiary'
  return (
    <div className="bg-surface-muted rounded-md px-3 py-2.5">
      <div className="text-xs text-ink-secondary">{label}</div>
      <div className="text-xl font-medium mt-0.5">{value}</div>
      {caption && <div className={`text-xs mt-0.5 ${captionClass}`}>{caption}</div>}
    </div>
  )
}

export function KpiStrip() {
  const { data, isLoading } = useQuery({ queryKey: ['kpis'], queryFn: api.kpis })

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3">
        {[0,1,2,3].map((i) => <div key={i} className="bg-surface-muted rounded-md h-[72px] animate-pulse" />)}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 md:gap-3">
      <Card
        label="Pending approvals"
        value={String(data.pending_approvals)}
        caption={data.overdue_approvals ? `${data.overdue_approvals} overdue` : 'on track'}
        tone={data.overdue_approvals ? 'danger' : 'neutral'}
      />
      <Card
        label="Scheduled this week"
        value={String(data.scheduled_this_week)}
        caption={`${data.scheduled_posts} posts · ${data.scheduled_social} social`}
      />
      <Card
        label="Writer spend MTD"
        value={formatCurrency(data.spend_mtd_cents)}
        caption={`of ${formatCurrency(data.budget_mtd_cents)} budget`}
      />
      <Card
        label="Organic sessions · 28d"
        value={formatCompact(data.organic_sessions_28d)}
        caption={`${data.organic_delta_pct > 0 ? '+' : ''}${data.organic_delta_pct.toFixed(1)}% vs prior`}
        tone={data.organic_delta_pct >= 0 ? 'success' : 'danger'}
      />
    </div>
  )
}
